import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandlerFn,
	HttpInterceptorFn,
	HttpRequest,
	HttpStatusCode,
} from '@angular/common/http'
import { catchError, Observable, switchMap, throwError } from 'rxjs'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { RefreshTokenResponseInterface } from '@modules/user-module/interfaces/refresh-token-response.interface'
import { AuthService } from '@modules/auth-module/services/auth.service'
import { captureHttpError } from '@shared/utils/capture-http-error'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { AlertService } from '@shared/services/alert.service'

export const errorsInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const alertService: AlertService = inject<AlertService>(AlertService)
	const translateService: TranslateService = inject<TranslateService>(TranslateService)
	const router: Router = inject<Router>(Router)
	const authService: AuthService = inject<AuthService>(AuthService)
	const userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	const tradingLauncherService: TradingLauncherService = inject<TradingLauncherService>(TradingLauncherService)

	const refresh: string = '/auth/token/refresh/'
	const login: string = '/auth_db/login'

	return next(req).pipe(
		catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
			switch (error.status) {
				case HttpStatusCode.Unauthorized:
					if (req.url.includes(refresh)) {
						alertService.error(translateService.instant('error.unauthorized'))
						userHelperService.dropUserData()
						authService.deleteTokens()
						tradingLauncherService.dropStates()
						router.navigate(['auth', 'sign-in'])
						return throwError(() => error)
					}
					if (req.url.includes(login)) return throwError(() => error)
					return authService.updateAccessByRefresh().pipe(
						switchMap((response: RefreshTokenResponseInterface) => {
							const clonedReq: HttpRequest<unknown> = req.clone({
								headers: req.headers.set('Authorization', `Bearer ${response.access}`),
							})
							return next(clonedReq)
						}),
					)
				case HttpStatusCode.Forbidden:
					alertService.error(translateService.instant('error.forbidden'))
					break
				case HttpStatusCode.InternalServerError:
					alertService.error(translateService.instant('error.server'))
					break
			}

			const shouldReportToSentry: boolean = [
				0,
				HttpStatusCode.BadRequest,
				HttpStatusCode.Unauthorized,
				HttpStatusCode.Forbidden,
				HttpStatusCode.NotFound,
				HttpStatusCode.Conflict,
				HttpStatusCode.UnprocessableEntity,
				HttpStatusCode.TooManyRequests,
				HttpStatusCode.RequestTimeout,
				HttpStatusCode.InternalServerError,
				HttpStatusCode.NotImplemented,
				HttpStatusCode.BadGateway,
				HttpStatusCode.ServiceUnavailable,
				HttpStatusCode.GatewayTimeout,
				HttpStatusCode.HttpVersionNotSupported,
			].includes(error.status)

			if (shouldReportToSentry) {
				captureHttpError(error, req)
			}

			return throwError((): HttpErrorResponse => error)
		}),
	)
}
